import React, {useEffect, useMemo, useRef} from 'react'
import {Box, Button, Flex, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {ChevronUpIcon, ChevronDownIcon, PauseIcon} from '../../../../../../leap/components/icons'
import {thumbsSlotRecipe} from './index.recipe'
import Image from '../../../../../../leap/components/cms/image'
import {hasValidImageSrc, isVideoAsset} from '../../../../../../leap/utils/utils'

const MIN_THUMBS_FOR_CONTROLS = 7

const Thumb = ({asset}) => {
    const isVideo = isVideoAsset(asset)

    const imageAsset = isVideo ? asset.poster : asset

    if (!hasValidImageSrc(imageAsset)) return null

    return (
        <Box position="relative" height="100%" width="100%">
            <Image {...imageAsset} objectFit="cover" aspectRatio={1} />

            {isVideo && (
                <Button
                    as="div"
                    variant="mediaGhost"
                    size="sm"
                    position="absolute"
                    bottom="spacing-6"
                    left="spacing-6"
                    pointerEvents="none"
                >
                    <PauseIcon />
                </Button>
            )}
        </Box>
    )
}

Thumb.propTypes = {
    asset: PropTypes.object
}

function ThumbsSlider({items, selectedIndex = 0, onSelect, onPrev, onNext, sliderId}) {
    const {formatMessage} = useIntl()

    const shouldHideControls = items.length < MIN_THUMBS_FOR_CONTROLS

    const recipe = useSlotRecipe({recipe: thumbsSlotRecipe})
    const styles = useMemo(() => recipe({shouldHideControls}), [recipe, shouldHideControls])

    const itemRefs = useRef([])

    useEffect(() => {
        const el = itemRefs.current[selectedIndex]
        el?.scrollIntoView({behavior: 'smooth', block: 'nearest'})
    }, [selectedIndex])

    return (
        <Flex
            role="region"
            aria-roledescription="carousel"
            id={sliderId ? 'slider-' + sliderId : undefined}
            {...styles.container}
        >
            <Flex {...styles.controlsContainer}>
                <Flex {...styles.buttonGroup}>
                    <Button
                        {...styles.navButton}
                        aria-controls={sliderId ? 'slider-' + sliderId : undefined}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_prev',
                            defaultMessage: 'Scroll previous'
                        })}
                        onClick={onPrev}
                        disabled={selectedIndex <= 0}
                    >
                        <ChevronUpIcon />
                    </Button>

                    <Button
                        {...styles.navButton}
                        aria-controls={sliderId ? 'slider-' + sliderId : undefined}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_next',
                            defaultMessage: 'Scroll next'
                        })}
                        onClick={onNext}
                        disabled={selectedIndex >= items.length - 1}
                    >
                        <ChevronDownIcon />
                    </Button>
                </Flex>
            </Flex>

            <Flex {...styles.scrollContainer}>
                {items.map((item, idx) => (
                    <Box
                        as="button"
                        key={idx}
                        ref={(el) => (itemRefs.current[idx] = el)}
                        {...(idx === selectedIndex && {'data-current': true})}
                        scrollSnapAlign="start"
                        scrollSnapStop="always"
                        onClick={() => onSelect?.(idx)}
                        {...styles.slide}
                    >
                        <Thumb asset={item}></Thumb>
                    </Box>
                ))}
            </Flex>
        </Flex>
    )
}

ThumbsSlider.propTypes = {
    items: PropTypes.array.isRequired,
    selectedIndex: PropTypes.number,
    onSelect: PropTypes.func,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    sliderId: PropTypes.string
}

export default ThumbsSlider
